import React, { useId, useState } from 'react'

interface FooterAccordionProps {
  title: string
  children: React.ReactNode
  defaultOpen?: boolean
  light?: boolean
}

interface AccordionTone {
  rule: string
  title: string
  chevron: string
  ring: string
}

// Mirrors FooterImpl's DARK_THEME / LIGHT_THEME. blue-800 on cream (light)
// because blue-500 fails AA as a text/glyph colour there.
const DARK_TONE: AccordionTone = {
  rule: 'border-white-500/15',
  title: 'text-white-500',
  chevron: 'text-blue-500',
  ring: 'focus-visible:outline-white-500'
}

const LIGHT_TONE: AccordionTone = {
  rule: 'border-ink-100/15',
  title: 'text-ink-700',
  chevron: 'text-blue-800',
  ring: 'focus-visible:outline-ink-700'
}

/**
 * Mobile footer section. The panel stays in the SSR HTML (link equity) but
 * is `display: none` while closed, so collapsed links are out of the tab
 * order. Opening fades in (opacity only — no height animation).
 */
function FooterAccordion({
  title,
  children,
  defaultOpen = false,
  light = false
}: FooterAccordionProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen)
  const panelId = useId()
  const tone = light ? LIGHT_TONE : DARK_TONE

  return (
    <div className={`border-b ${tone.rule}`}>
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className={`flex min-h-14 w-full items-center justify-between gap-4 py-3 text-left focus-visible:outline-2 focus-visible:-outline-offset-2 ${tone.ring}`}
        aria-expanded={isOpen}
        aria-controls={panelId}
      >
        <span
          className={`font-display text-[17px] leading-snug font-medium ${tone.title}`}
        >
          {title}
        </span>
        <i
          aria-hidden="true"
          className={`si si-down text-[10px] transition-transform duration-300 motion-reduce:transition-none ${tone.chevron} ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>
      <div
        id={panelId}
        className={isOpen ? 'motion-safe:animate-fade-in' : 'hidden'}
      >
        <ul className="pb-4">{children}</ul>
      </div>
    </div>
  )
}

export default React.memo(FooterAccordion)
